import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, type SetStateAction } from 'react'

// Bridge existing listener-owned state into the existing session QueryClient.
// No query function, polling, persistent storage or additional subscription.
// Domain hooks still own parsing, readiness, errors and listener cleanup.
export function useRetainedRealtimeState<T>(
  identity: readonly (string | number | boolean | null)[],
  initial: T,
  reusable: (state: T) => boolean
) {
  const client = useQueryClient()
  const identityKey = JSON.stringify(identity)
  const key = useMemo(
    () =>
      [
        'realtime-snapshot',
        ...(JSON.parse(identityKey) as (string | number | boolean | null)[]),
      ] as const,
    [identityKey]
  )
  const defaults = useRef({ initial, reusable })
  defaults.current = { initial, reusable }
  const query = useQuery<T>({ queryKey: key, queryFn: skipToken })
  const setState = useCallback(
    (update: SetStateAction<T>) => {
      client.setQueryData<T>(key, (current) =>
        typeof update === 'function'
          ? (update as (previous: T) => T)(current ?? defaults.current.initial)
          : update
      )
    },
    [client, key]
  )
  const resume = useCallback(() => {
    setState((current) =>
      defaults.current.reusable(current) ? current : defaults.current.initial
    )
  }, [setState])
  return [query.data ?? initial, setState, resume] as const
}
